import { useState, useRef, useEffect, useMemo } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Inbox, Plus, GripVertical, ChevronDown, FolderIcon, Pencil } from 'lucide-react';
import { Task, Folder } from '@/types/task';
import { AddTaskForm } from '@/components/AddTaskForm';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface BacklogSidebarProps {
  tasks: Task[];
  folders?: Folder[];
  onAddTask: (content: string, duration?: number, dueTime?: string) => Promise<void>;
  onToggle?: (id: string) => void;
  onDelete?: (id: string) => void;
  onMoveToToday: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onUpdateFolder?: (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => Promise<void>;
}

function BacklogTaskItem({ 
  task, 
  priorityNumber,
  folders = [],
  onMoveToToday,
  onUpdateTask,
  isDragging,
  dragHandleProps 
}: { 
  task: Task; 
  priorityNumber: number;
  folders?: Folder[];
  onMoveToToday: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  isDragging: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}) {
  const taskFolder = folders.find(f => f.id === task.folder_id);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(task.content);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (editValue.trim() && editValue !== task.content) {
      onUpdateTask(task.id, { content: editValue.trim() });
    } else {
      setEditValue(task.content);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(task.content);
      setIsEditing(false);
    }
  };

  const handleTextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  return (
    <div
      className={cn(
        "group flex items-center gap-3 p-4 rounded-lg border bg-card/50 touch-manipulation transition-shadow duration-200",
        isDragging && "shadow-lg ring-2 ring-primary/20 bg-card"
      )}
    >
      {/* Priority Number Badge */}
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
        {priorityNumber}
      </div>
      
      {isEditing ? (
        <Input
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="flex-1 h-8 text-sm py-0 px-2 touch-manipulation"
        />
      ) : (
        <span 
          className="flex-1 text-sm break-words cursor-text hover:bg-muted/50 rounded px-1 -mx-1 touch-manipulation"
          onClick={handleTextClick}
        >
          {task.content}
        </span>
      )}
      
      {/* Folder indicator */}
      {taskFolder && (
        <span className="flex items-center gap-1 shrink-0">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: taskFolder.color }} />
          <span className="text-xs text-muted-foreground">{taskFolder.name}</span>
        </span>
      )}

      {task.duration && (
        <span className="text-xs text-muted-foreground shrink-0">
          {task.duration}m
        </span>
      )}
      
      {/* Add to today button - always visible */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 text-primary touch-manipulation"
              onClick={() => onMoveToToday(task.id)}
            >
              <Plus className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Add to today</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {/* Drag Handle - Right side */}
      <div
        {...dragHandleProps}
        className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground p-2 -m-2 touch-manipulation"
      >
        <GripVertical className="h-6 w-6" />
      </div>
    </div>
  );
}

/** Single droppable list (like Today) - drag to any position, items shift to make room */
function BacklogList({
  tasks,
  folders = [],
  onMoveToToday,
  onUpdateTask,
}: {
  tasks: Task[];
  folders?: Folder[];
  onMoveToToday: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
}) {
  return (
    <div className="flex-1 min-h-[200px] rounded-lg border border-dashed border-muted-foreground/20 overflow-y-auto transition-[border-color] duration-200">
      <Droppable droppableId="backlog">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={cn(
              "p-2 min-h-[180px] rounded-lg space-y-2",
              snapshot.isDraggingOver ? "bg-primary/5" : "bg-muted/5"
            )}
          >
            {tasks.length === 0 && !snapshot.isDraggingOver ? (
              <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                <Plus className="h-6 w-6 text-muted-foreground/30 mb-2" />
                <p className="text-xs text-muted-foreground/60">No tasks yet</p>
              </div>
            ) : (
              <>
                {tasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(dragProvided, dragSnapshot) => {
                      const getTransitionStyle = () => {
                        if (dragSnapshot.isDragging && !dragSnapshot.isDropAnimating) return 'none';
                        if (dragSnapshot.isDropAnimating) return undefined;
                        return 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
                      };
                      return (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className="w-full min-w-0"
                          style={{
                            ...dragProvided.draggableProps.style,
                            transition: getTransitionStyle(),
                          }}
                        >
                          <BacklogTaskItem
                            task={task}
                            priorityNumber={index + 1}
                            folders={folders}
                            onMoveToToday={onMoveToToday}
                            onUpdateTask={onUpdateTask}
                            isDragging={dragSnapshot.isDragging}
                            dragHandleProps={dragProvided.dragHandleProps || undefined}
                          />
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}

export function BacklogSidebar({ 
  tasks, 
  folders = [],
  onAddTask, 
  onMoveToToday,
  onUpdateTask,
  onUpdateFolder,
}: Omit<BacklogSidebarProps, 'onToggle' | 'onDelete'>) {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    const folderMap = new Map<string | null, Task[]>();
    for (const folder of folders) folderMap.set(folder.id, []);
    folderMap.set(null, []);
    for (const task of tasks) {
      const key = folderMap.has(task.folder_id) ? task.folder_id : null;
      folderMap.get(key)!.push(task);
    }
    const result: { folder: Folder | null; tasks: Task[] }[] = [];
    for (const folder of folders) {
      const ft = folderMap.get(folder.id) || [];
      if (ft.length > 0) result.push({ folder, tasks: ft });
    }
    const unfiled = folderMap.get(null) || [];
    if (unfiled.length > 0) result.push({ folder: null, tasks: unfiled });
    return result;
  }, [tasks, folders]);

  const toggleCollapse = (id: string) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // If no folders or only one unfiled group, use flat list
  const useFlatList = folders.length === 0 || (groups.length === 1 && groups[0].folder === null);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <Inbox className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-medium text-muted-foreground">All Todos</h2>
        <span className="text-xs text-muted-foreground/60 ml-auto">
          {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
        </span>
      </div>
      
      <div className="mb-3" data-tour-target="backlog-add-task">
        <AddTaskForm 
          onAdd={onAddTask} 
          placeholder="Add to backlog..." 
          compact
          autoFocus={false}
          preventMobileFocus
        />
      </div>
      
      {useFlatList ? (
        <BacklogList
          tasks={tasks}
          folders={folders}
          onMoveToToday={onMoveToToday}
          onUpdateTask={onUpdateTask}
        />
      ) : (
        <div className="flex-1 min-h-[200px] overflow-y-auto space-y-3">
          {groups.map((group) => {
            const groupId = group.folder?.id ?? 'unfiled';
            const isOpen = !collapsedIds.has(groupId);
            return (
              <BacklogFolderSection
                key={groupId}
                group={group}
                isOpen={isOpen}
                onToggle={() => toggleCollapse(groupId)}
                onMoveToToday={onMoveToToday}
                onUpdateTask={onUpdateTask}
                onUpdateFolder={onUpdateFolder}
                folders={folders}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function BacklogFolderSection({
  group,
  isOpen,
  onToggle,
  onMoveToToday,
  onUpdateTask,
  onUpdateFolder,
  folders,
}: {
  group: { folder: Folder | null; tasks: Task[] };
  isOpen: boolean;
  onToggle: () => void;
  onMoveToToday: (id: string) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onUpdateFolder?: (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => Promise<void>;
  folders: Folder[];
}) {
  const folder = group.folder;
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder?.name ?? '');
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleRenameSave = () => {
    if (folder && renameValue.trim() && renameValue.trim() !== folder.name && onUpdateFolder) {
      onUpdateFolder(folder.id, { name: renameValue.trim() });
    } else {
      setRenameValue(folder?.name ?? '');
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRenameSave();
    else if (e.key === 'Escape') {
      setRenameValue(folder?.name ?? '');
      setIsRenaming(false);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <div className="flex items-center gap-2 py-1.5 group/folder-header">
        <CollapsibleTrigger className="flex items-center gap-2 flex-1 text-left">
          <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", !isOpen && "-rotate-90")} />
          {folder ? (
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: folder.color }} />
          ) : (
            <FolderIcon className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {isRenaming ? (
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleRenameSave}
              onKeyDown={handleRenameKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="h-6 text-sm py-0 px-1 w-32"
            />
          ) : (
            <span className="text-sm font-medium text-muted-foreground">{folder ? folder.name : 'Unfiled'}</span>
          )}
          <span className="text-xs text-muted-foreground/60 ml-1">{group.tasks.length}</span>
        </CollapsibleTrigger>
        {folder && onUpdateFolder && !isRenaming && (
          <button
            onClick={(e) => { e.stopPropagation(); setRenameValue(folder.name); setIsRenaming(true); }}
            className="opacity-100 sm:opacity-0 sm:group-hover/folder-header:opacity-100 transition-opacity p-1 rounded hover:bg-muted text-muted-foreground"
            aria-label="Rename folder"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      <CollapsibleContent className="pt-1 pl-4 space-y-2">
        {group.tasks.map((task, index) => (
          <BacklogTaskItem
            key={task.id}
            task={task}
            priorityNumber={index + 1}
            folders={folders}
            onMoveToToday={onMoveToToday}
            onUpdateTask={onUpdateTask}
            isDragging={false}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
